import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, ShieldCheck, Copy, Receipt } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";
import OrderSummaryItem from "@/components/OrderSummaryItem";

export default function Checkout() {
  const { items, clear } = useCart();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const savedForm = (() => { try { return JSON.parse(sessionStorage.getItem("cityshor_checkout_form") || "null"); } catch { return null; } })();
  const savedPayment = sessionStorage.getItem("cityshor_checkout_payment") || "cod";

  const [form, setForm] = useState(savedForm || {
    full_name: user?.full_name || "", phone: "", email: user?.email || "",
    address_line1: "", address_line2: "", city: "", state: "", pincode: "",
    gender: "", age: "",
  });
  const [payment, setPayment] = useState(savedPayment);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [offerCode, setOfferCode] = useState("");
  const [appliedOfferCode, setAppliedOfferCode] = useState("");
  const [productDetails, setProductDetails] = useState({});

  // Fetch full product details for every cart line so the order summary can show
  // name, code, description, seller, rating, reviews without duplicating cart state.
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set((items || []).map((i) => i.product_id)));
    const missing = ids.filter((id) => !productDetails[id]);
    if (missing.length === 0) return;
    (async () => {
      const entries = await Promise.all(missing.map(async (id) => {
        try { const r = await api.get(`/products/${id}`); return [id, r.data]; }
        catch { return [id, null]; }
      }));
      if (cancelled) return;
      setProductDetails((prev) => {
        const next = { ...prev };
        for (const [id, p] of entries) if (p) next[id] = p;
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [items.map((i) => i.product_id).join(",")]);

  const validate = () => {
    const e = {};
    if (!form.full_name?.trim()) e.full_name = "Full name is required";
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length !== 10) e.phone = "Phone number must be exactly 10 digits";
    const emailVal = (form.email || "").trim();
    if (/\s/.test(form.email || "")) e.email = "Email must not contain spaces";
    else if (!/^\S+@\S+\.\S+$/.test(emailVal)) e.email = "Enter a valid email";
    if (!form.address_line1?.trim()) e.address_line1 = "Address is required";
    if (!form.city?.trim()) e.city = "City is required";
    if (!form.state?.trim()) e.state = "State is required";
    if (!/^\d{6}$/.test((form.pincode || "").trim())) e.pincode = "Enter a valid 6 digit pincode";
    if (!["male", "female", "other"].includes(form.gender)) e.gender = "Please select gender";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    if (user && !form.full_name && !form.email) {
      setForm((prev) => ({ ...prev, full_name: user.full_name || prev.full_name, email: user.email || prev.email }));
    }
    // eslint-disable-next-line
  }, [user]);

  // Fetch authoritative price quote (GST added on top) whenever cart, pincode or payment method changes.
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      pincode: /^\d{6}$/.test(form.pincode || "") ? form.pincode : null,
      payment_method: payment,
      offer_code: appliedOfferCode || null,
    };
    api.post("/cart/quote", payload).then((r) => setQuote(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [items, form.pincode, payment, appliedOfferCode]);

  // If COD gets gated off (admin switch, min order value, vendor toggle), fall back to online.
  useEffect(() => {
    if (quote && quote.cod_available === false && payment === "cod") {
      setPayment("online");
      toast.info(quote.cod_unavailable_reason || "COD is not available for this order.");
    }
    // eslint-disable-next-line
  }, [quote]);

  const upd = (k) => (e) => {
    const raw = e?.target?.value ?? e;
    let v = raw;
    if (k === "phone") v = String(raw).replace(/\D/g, "").slice(0, 10);
    else if (k === "email") v = String(raw).replace(/\s+/g, "");
    else if (k === "pincode") v = String(raw).replace(/\D/g, "").slice(0, 6);
    setForm({ ...form, [k]: v });
  };
  const gstin = config?.tax?.gstin || "";
  const shipZone = quote?.shipping_zone || "";
  const shipEta = quote?.shipping_eta || "";
  const shipping = quote?.shipping ?? 0;
  const packaging = quote?.packaging ?? 0;
  const productGst = quote?.product_gst ?? 0;
  const ancillaryGst = (quote?.packaging_gst ?? 0) + (quote?.shipping_gst ?? 0);
  const taxes = +(productGst + ancillaryGst).toFixed(2);
  const totalGst = quote?.total_gst ?? 0;
  const codCharges = quote?.cod_charges ?? 0;
  const platformFee = quote?.platform_fee ?? 0;
  const subtotalBase = quote?.subtotal ?? 0;
  const total = quote?.total ?? 0;

  const copy = (text) => { if (!text) return; navigator.clipboard?.writeText(text); toast.success("Copied"); };

  if (items.length === 0) return <div className="mx-auto max-w-4xl px-4 py-20 text-center">Your cart is empty. <a href="/" className="text-[#F48E2E]">Shop now</a>.</div>;
  if (authLoading) return <div className="mx-auto max-w-4xl px-4 py-20 text-center">Loading...</div>;

  const submit = async () => {
    if (!user) {
      sessionStorage.setItem("cityshor_checkout_form", JSON.stringify(form));
      sessionStorage.setItem("cityshor_checkout_payment", payment);
      toast.info("Please log in or sign up to place your order.");
      nav("/login?next=/checkout");
      return;
    }
    if (!validate()) return toast.error("Please fill all required fields");
    if (!acceptTerms) return toast.error("Please accept the Terms and Conditions to place the order.");
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address: { ...form, age: form.age ? Number(form.age) : null, gender: form.gender || null },
        payment_method: payment,
        accept_terms: acceptTerms,
        offer_code: appliedOfferCode || null,
      };
      const { data } = await api.post("/orders/checkout", payload);
      // Order accepted by backend — safe to clear the saved checkout draft now.
      sessionStorage.removeItem("cityshor_checkout_form");
      sessionStorage.removeItem("cityshor_checkout_payment");
      if (payment === "cod" || payment === "bank_transfer") {
        clear();
        toast.success(payment === "cod" ? "Order placed. Track it in My Orders." : "Order created. Please complete the bank transfer using the details shown, then WhatsApp us the reference.");
        nav(`/orders/success/${data.order.id}`);
      } else {
        const rzp = data.razorpay_order;
        const isMock = rzp.id.startsWith("order_mock_");
        if (isMock) {
          const verify = await api.post("/orders/verify-payment", { order_id: data.order.id, razorpay_order_id: rzp.id, razorpay_payment_id: "pay_mock_" + Date.now(), razorpay_signature: "sig_mock" });
          if (verify.data.verified) { clear(); toast.success("Payment successful (test mode)"); nav(`/orders/success/${data.order.id}`); }
          else toast.error("Payment verification failed");
        } else {
          const options = {
            key: data.razorpay_key_id, amount: rzp.amount, currency: "INR", order_id: rzp.id,
            name: "Cityshor LLP", description: "Order payment",
            prefill: { name: form.full_name, email: form.email, contact: form.phone },
            handler: async (res) => {
              const verify = await api.post("/orders/verify-payment", { order_id: data.order.id, razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
              if (verify.data.verified) { clear(); toast.success("Payment successful"); nav(`/orders/success/${data.order.id}`); }
              else toast.error("Payment verification failed");
            },
            modal: {
              ondismiss: () => {
                clear();
                toast.info("Payment not completed. Your order is saved as Payment pending. You can complete the payment from this page within 30 minutes.");
                nav(`/orders/${data.order.id}`);
              },
            },
            theme: { color: "#F48E2E" },
          };
          if (window.Razorpay) new window.Razorpay(options).open();
          else toast.error("Razorpay SDK not loaded");
        }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 lg:px-8 py-10" data-testid="checkout-page">
      <h1 className="font-display text-4xl mb-4">Checkout</h1>

      {!user && (
        <div data-testid="guest-checkout-banner" className="mb-6 p-4 rounded-2xl bg-[#FEF3E6] border border-[#F48E2E]/30 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-800">You are checking out as a guest. You will be asked to log in or sign up before we place the order.</div>
          <div className="flex gap-2">
            <Button data-testid="guest-login-btn" onClick={() => nav("/login?next=/checkout")} variant="outline" className="rounded-full">Log in</Button>
            <Button data-testid="guest-signup-btn" onClick={() => nav("/register?next=/checkout")} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">Sign up</Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-4">
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Delivery Details</div>
            <p className="text-xs text-gray-500 -mt-2 mb-3">All fields marked <span className="text-red-500">*</span> are required.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full Name <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-name" value={form.full_name} onChange={upd("full_name")} aria-invalid={!!errors.full_name} className={errors.full_name ? "border-red-400" : ""} />
                {errors.full_name && <p data-testid="err-name" className="text-[11px] text-red-500 mt-1">{errors.full_name}</p>}
              </div>
              <div>
                <Label>Phone <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-phone" inputMode="numeric" maxLength={10} placeholder="10 digit mobile" value={form.phone} onChange={upd("phone")} aria-invalid={!!errors.phone} className={errors.phone ? "border-red-400" : ""} />
                {errors.phone && <p data-testid="err-phone" className="text-[11px] text-red-500 mt-1">{errors.phone}</p>}
              </div>
              <div className="col-span-2">
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-email" type="email" value={form.email} onChange={upd("email")} aria-invalid={!!errors.email} className={errors.email ? "border-red-400" : ""} />
                {errors.email && <p data-testid="err-email" className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div className="col-span-2">
                <Label>Address Line 1 <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-addr1" placeholder="House / flat, street, area" value={form.address_line1} onChange={upd("address_line1")} aria-invalid={!!errors.address_line1} className={errors.address_line1 ? "border-red-400" : ""} />
                {errors.address_line1 && <p data-testid="err-addr" className="text-[11px] text-red-500 mt-1">{errors.address_line1}</p>}
              </div>
              <div className="col-span-2">
                <Label>Address Line 2 <span className="text-gray-400 font-normal">optional</span></Label>
                <Input data-testid="ck-addr2" placeholder="Landmark, apartment" value={form.address_line2} onChange={upd("address_line2")} />
              </div>
              <div>
                <Label>City <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-city" value={form.city} onChange={upd("city")} aria-invalid={!!errors.city} className={errors.city ? "border-red-400" : ""} />
                {errors.city && <p data-testid="err-city" className="text-[11px] text-red-500 mt-1">{errors.city}</p>}
              </div>
              <div>
                <Label>State <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-state" value={form.state} onChange={upd("state")} aria-invalid={!!errors.state} className={errors.state ? "border-red-400" : ""} />
                {errors.state && <p data-testid="err-state" className="text-[11px] text-red-500 mt-1">{errors.state}</p>}
              </div>
              <div>
                <Label>Pincode <span className="text-red-500">*</span></Label>
                <Input data-testid="ck-pincode" inputMode="numeric" maxLength={6} value={form.pincode} onChange={upd("pincode")} placeholder="6 digits" aria-invalid={!!errors.pincode} className={errors.pincode ? "border-red-400" : ""} />
                {form.pincode && form.pincode.length === 6 && !errors.pincode && (
                  <div className="text-[11px] text-gray-500 mt-1">{shipZone} · Shipping ₹{shipping} · {shipEta}</div>
                )}
                {errors.pincode && <p data-testid="err-pincode" className="text-[11px] text-red-500 mt-1">{errors.pincode}</p>}
              </div>
              <div>
                <Label>Gender <span className="text-red-500">*</span></Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="ck-gender" aria-invalid={!!errors.gender} className={errors.gender ? "border-red-400" : ""}><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
                {errors.gender && <p data-testid="err-gender" className="text-[11px] text-red-500 mt-1">{errors.gender}</p>}
              </div>
              <div className="col-span-2"><Label>Age <span className="text-gray-400 font-normal">optional</span></Label><Input data-testid="ck-age" type="number" value={form.age} onChange={upd("age")} /></div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Payment Method</div>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
              {quote && quote.cod_available === false ? (
                <label className="flex items-center gap-3 p-3 border rounded-xl opacity-50 cursor-not-allowed" data-testid="pay-cod-disabled">
                  <RadioGroupItem value="cod" disabled />
                  <div><div className="font-medium">Cash on Delivery</div><div className="text-xs text-gray-500" data-testid="cod-unavailable-reason">{quote.cod_unavailable_reason || "COD is not available for this order."}</div></div>
                </label>
              ) : (
                <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer">
                  <RadioGroupItem value="cod" data-testid="pay-cod" />
                  <div><div className="font-medium">Cash on Delivery</div><div className="text-xs text-gray-500">Pay when you receive. Extra Rs 100 COD handling charge applies.</div></div>
                </label>
              )}
              <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer">
                <RadioGroupItem value="online" data-testid="pay-online" />
                <div><div className="font-medium">Online Payment (Razorpay)</div><div className="text-xs text-gray-500">UPI, Cards, Netbanking and Wallets. No extra charge.</div></div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer">
                <RadioGroupItem value="bank_transfer" data-testid="pay-bank" />
                <div><div className="font-medium">Bank Transfer / UPI (manual)</div><div className="text-xs text-gray-500">Bank details will be shared with you after you accept the terms and place the order.</div></div>
              </label>
            </RadioGroup>
          </div>

          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="ck-accept-terms" checked={acceptTerms} onCheckedChange={setAcceptTerms} className="mt-1" />
              <div className="text-sm text-gray-700">
                I agree to the <Link to="/legal/buyer-terms" target="_blank" className="text-[#F48E2E] hover:underline">Buyer Terms &amp; Conditions</Link>, the <Link to="/legal/returns-refunds" target="_blank" className="text-[#F48E2E] hover:underline">Returns &amp; Refunds Policy</Link> and the <Link to="/legal/shipping-delivery" target="_blank" className="text-[#F48E2E] hover:underline">Shipping &amp; Delivery Policy</Link>. I understand that product images are for reference and the actual product may vary slightly in colour, texture or handcrafted detail. Return shipping charges are borne by the customer for reasons other than seller error.
              </div>
            </label>
          </div>
        </div>

        <div className="p-6 bg-white rounded-2xl border border-gray-200 h-fit">
          <div className="font-display text-xl mb-4">Order Summary</div>
          <div data-testid="order-summary-items" className="mb-3">
            {items.map((it) => (
              <OrderSummaryItem
                key={`${it.product_id}-${it.size || "_"}`}
                item={it}
                product={productDetails[it.product_id]}
                size={it.size}
              />
            ))}
          </div>
          <div className="border-t mt-3 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="sum-subtotal">₹{subtotalBase.toLocaleString("en-IN")}</span></div>
            {quote?.offer && (
              <div className="flex justify-between text-green-700" data-testid="sum-offer">
                <span>Offer ({quote.offer.code} · {quote.offer.discount_percent}% off)</span>
                <span>− ₹{(quote.offer.product_discount + (quote.offer.gst_discount || 0)).toLocaleString("en-IN")}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-600"><span>Taxes (total GST)</span><span data-testid="sum-gst">₹{taxes.toLocaleString("en-IN")}</span></div>
            <div className="pl-3 -mt-1 space-y-0.5 text-[11px] text-gray-500" data-testid="gst-breakdown">
              <div className="flex justify-between"><span>Product GST</span><span>₹{productGst.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span>Packaging GST</span><span>₹{(quote?.packaging_gst ?? 0).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span>Shipping GST</span><span>₹{(quote?.shipping_gst ?? 0).toLocaleString("en-IN")}</span></div>
            </div>
            <div className="flex justify-between text-gray-600"><span>Packaging</span><span>₹{packaging.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-gray-600">
              <span>Shipping{shipZone ? ` (${shipZone})` : ""}</span>
              <span>{quote?.free_delivery ? <span className="text-green-700">Free</span> : `₹${shipping.toLocaleString("en-IN")}`}</span>
            </div>
            {!quote?.free_delivery && (quote?.shipping_breakdown || []).length > 0 && (
              <div data-testid="shipping-breakdown" className="pl-3 -mt-1 space-y-0.5">
                {quote.shipping_breakdown.map((b) => (
                  <div key={b.vendor_id} className="flex justify-between text-[11px] text-gray-500">
                    <span>{b.vendor_name}{b.courier ? ` · ${b.courier}` : ""}{b.eta ? ` · ${b.eta}` : ""}</span>
                    <span>₹{Number(b.amount).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            )}
            {platformFee > 0 && (
              <div className="flex justify-between text-gray-600" data-testid="platform-fee-line"><span>Platform fee <span className="text-[10px] text-gray-400">(no GST)</span></span><span>₹{platformFee.toLocaleString("en-IN")}</span></div>
            )}
            {payment === "cod" && codCharges > 0 && (
              <div className="flex justify-between text-gray-600" data-testid="cod-charge-line"><span>COD handling</span><span>₹{codCharges.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between font-display text-lg pt-2 border-t mt-2"><span>Total</span><span data-testid="sum-total">₹{total.toLocaleString("en-IN")}</span></div>
          </div>

          <div className="mt-4 border-t pt-4">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Have an offer code?</Label>
            {appliedOfferCode ? (
              <div className="mt-2 flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-3 py-2">
                <div className="text-sm text-green-800">
                  <span className="font-semibold">{appliedOfferCode}</span> applied
                  {quote?.offer && <span className="text-xs text-green-700"> · {quote.offer.discount_percent}% off{quote.offer.free_delivery ? " · Free delivery" : ""}</span>}
                </div>
                <button
                  data-testid="offer-remove"
                  type="button"
                  onClick={() => { setAppliedOfferCode(""); setOfferCode(""); }}
                  className="text-xs text-red-500 hover:text-red-600"
                >Remove</button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  data-testid="offer-code-input"
                  value={offerCode}
                  onChange={(e) => setOfferCode(e.target.value.replace(/\s+/g, "").toUpperCase())}
                  placeholder="Enter code"
                  className="h-9 uppercase"
                />
                <Button
                  data-testid="offer-apply"
                  type="button"
                  variant="outline"
                  className="rounded-full h-9"
                  onClick={async () => {
                    const code = offerCode.trim();
                    if (!code) return toast.error("Enter a code");
                    try {
                      const payload = {
                        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                        pincode: /^\d{6}$/.test(form.pincode || "") ? form.pincode : null,
                        payment_method: payment,
                        offer_code: code,
                      };
                      const { data } = await api.post("/cart/quote", payload);
                      if (data.offer) {
                        setAppliedOfferCode(code);
                        toast.success(`Offer ${code} applied`);
                      } else {
                        toast.error("This code is not valid for your cart");
                      }
                    } catch { toast.error("Could not apply this offer"); }
                  }}
                >Apply</Button>
              </div>
            )}
          </div>

          <div className="mt-2 text-[11px] text-gray-500">Product prices shown on the website are exclusive of GST. Applicable GST is added at checkout as per the tax slab of each product.</div>
          {gstin && (
            <div className="mt-3 text-[10px] text-gray-500 flex items-center gap-1"><Receipt className="h-3 w-3" /> GSTIN {gstin} · Cityshor LLP</div>
          )}
          <div className="mt-4 text-xs text-gray-600 text-center" data-testid="checkout-refund-notice">
            By placing this order you agree to our <Link to="/refund-policy" target="_blank" rel="noreferrer" className="text-[#F48E2E] underline hover:text-[#D97815]">Return &amp; Refund Policy</Link>.
          </div>
          <Button data-testid="place-order-btn" onClick={submit} disabled={busy || !acceptTerms} className="w-full mt-3 rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-11 disabled:opacity-50">
            {busy ? "Processing..." : payment === "cod" ? "Place Order (COD)" : payment === "bank_transfer" ? "Place Order (Bank Transfer)" : "Pay Now"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
        <div className={`truncate ${mono ? "font-mono-tab" : ""} text-sm text-gray-900`}>{value || "-"}</div>
      </div>
      {value && <button type="button" onClick={onCopy} className="text-gray-400 hover:text-[#F48E2E]" aria-label={`Copy ${label}`}><Copy className="h-3.5 w-3.5" /></button>}
    </div>
  );
}
